import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Select,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type { Assessment, AssessmentStatus, Vendor } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { vendorApi } from "../../lib/api";
import { VendorDemoBanner } from "../../components/VendorMode";
import { ASSESSMENT_STATUS_LABEL, assessmentStatusTone } from "./labels";
import { message, usePeople } from "./helpers";

const STATUSES: AssessmentStatus[] = ["OPEN", "SUBMITTED", "CANCELLED"];

export function VenAssessments() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [status, setStatus] = useState<AssessmentStatus>("OPEN");
  const [opening, setOpening] = useState(false);
  const people = usePeople(orgId);

  const state = useAsync(async () => vendorApi.assessments(orgId, { status }), [orgId, status]);
  const assessments = state.data?.data.assessments ?? [];
  const demoMode = state.data?.demoMode ?? false;

  const vendors = useAsync(async () => (await vendorApi.vendors(orgId)).data.vendors, [orgId]);
  const vendorName = (id: string): string =>
    (vendors.data ?? []).find((v) => v.id === id)?.legalName ?? id;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Assessments</h1>
          <p>
            What we asked a vendor, what they answered, and who is answerable for it. Vendor AI holds the
            answers; nothing here is a copy.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end" }}>
          <div style={{ minWidth: "10rem" }}>
            <Field label="Status">
              {(id) => (
                <Select
                  id={id}
                  value={status}
                  onChange={(e) => setStatus(e.target.value as AssessmentStatus)}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {ASSESSMENT_STATUS_LABEL[s]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <Button onClick={() => setOpening(true)}>Start an assessment</Button>
        </div>
      </div>

      <VendorDemoBanner show={demoMode} />

      {state.loading && !state.data && <LoadingState label="Loading assessments…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
      {state.data && assessments.length === 0 && (
        <EmptyState
          title="No assessment is in this state"
          description="Start one against a vendor to ask it the baseline questions."
        />
      )}

      {assessments.length > 0 && (
        <Card>
          <CardHeader title="Assessments" />
          <CardBody>
            <DataTable
              columns={[
                {
                  key: "title",
                  header: "Assessment",
                  render: (a: Assessment) => (
                    <Link to={`/studio/vendor-ai/assessments/${a.id}`}>{a.title}</Link>
                  ),
                },
                {
                  key: "vendorId",
                  header: "Vendor",
                  render: (a: Assessment) => (
                    <Link to={`/studio/vendor-ai/vendors/${a.vendorId}`}>{vendorName(a.vendorId)}</Link>
                  ),
                },
                {
                  key: "status",
                  header: "Status",
                  render: (a: Assessment) => (
                    <Badge tone={assessmentStatusTone(a.status)}>{ASSESSMENT_STATUS_LABEL[a.status]}</Badge>
                  ),
                },
                {
                  key: "assignedTo",
                  header: "Answering",
                  render: (a: Assessment) => people.name(a.assignedTo),
                },
                {
                  key: "dueOn",
                  header: "Due",
                  render: (a: Assessment) => (a.dueOn ? formatDate(a.dueOn) : "No date set"),
                },
                {
                  key: "requiredOutstanding",
                  header: "Outstanding",
                  render: (a: Assessment) =>
                    a.requiredOutstanding > 0 ? (
                      <Badge tone="warning">{a.requiredOutstanding} required unanswered</Badge>
                    ) : (
                      <span style={{ color: "var(--ods-color-muted)" }}>
                        {a.answeredCount} answered
                      </span>
                    ),
                },
              ]}
              rows={assessments}
              caption="Vendor assessments"
            />
          </CardBody>
        </Card>
      )}

      <OpenAssessmentDialog
        open={opening}
        orgId={orgId}
        vendors={vendors.data ?? []}
        onClose={() => setOpening(false)}
      />
    </>
  );
}

function OpenAssessmentDialog({
  open,
  orgId,
  vendors,
  onClose,
}: {
  open: boolean;
  orgId: string;
  vendors: Vendor[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const people = usePeople(orgId);
  const templates = useAsync(async () => (await vendorApi.templates(orgId)).data.templates, [orgId]);
  const [vendorId, setVendorId] = useState("");
  const [versionId, setVersionId] = useState("");
  const [title, setTitle] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only a published version can be answered against: an editable one would let
  // two vendors answer different questions under the same version identifier.
  const versions = (templates.data ?? []).flatMap((t) =>
    t.versions
      .filter((v) => v.status === "PUBLISHED")
      .map((v) => ({ id: v.id, label: `${t.label} — version ${v.version} (${v.questionCount} questions)` })),
  );

  async function start() {
    setSaving(true);
    setError(null);
    try {
      const res = await vendorApi.openAssessment(orgId, vendorId, {
        templateVersionId: versionId,
        title: title.trim() === "" ? null : title.trim(),
        assignedTo: assignedTo === "" ? null : assignedTo,
        dueOn: dueOn === "" ? null : dueOn,
      });
      onClose();
      navigate(`/studio/vendor-ai/assessments/${res.data.assessment.id}`);
    } catch (err) {
      setError(message(err, "Could not start this assessment."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      title="Start an assessment"
      description="The questions are pinned at the version you pick, so the answers stay readable against what was asked."
      onClose={onClose}
      dismissible={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={start} disabled={saving || vendorId === "" || versionId === ""}>
            {saving ? "Starting…" : "Start"}
          </Button>
        </>
      }
    >
      {error && <p className="ods-failclosed">{error}</p>}
      <Field label="Vendor">
        {(id) => (
          <Select id={id} value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
            <option value="">Choose a vendor</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.legalName}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Questionnaire">
        {(id) => (
          <Select id={id} value={versionId} onChange={(e) => setVersionId(e.target.value)}>
            <option value="">Choose a published version</option>
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Title" help="Optional. Defaults to the questionnaire name.">
        {(id) => <TextInput id={id} value={title} onChange={(e) => setTitle(e.target.value)} />}
      </Field>
      <Field label="Who answers it" help="Optional, and changeable later.">
        {(id) => (
          <Select id={id} value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
            <option value="">Nobody yet</option>
            {people.members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName || m.email}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Due" help="Vendor AI owns this date. Nothing else may change it.">
        {(id) => <TextInput id={id} type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />}
      </Field>
    </Dialog>
  );
}
